/**
 * Plandalf support chat widget.
 *
 * Dependency-free, renders into a shadow root, talks to /api/support on
 * the Plandalf app. One conversation per visitor; each conversation is a
 * Slack thread on our side. See specs/support-chat.md.
 *
 * Embed:
 *   <script src="https://admin.plandalf.dev/js/support.js" data-theme="dark"></script>
 * or drive it:
 *   PlandalfSupport.init({ apiBase, theme, surface, launcher });
 *   PlandalfSupport.open({ message? }) / close() / toggle()
 */
import { styles } from './styles';

type Theme = 'dark' | 'light';
type Surface = 'site' | 'app';
type Launcher = 'pill' | 'compact';

export interface InitOptions {
  apiBase?: string;
  /** Route prefix; the app surface uses its session-authenticated twin. */
  apiPath?: string;
  theme?: Theme;
  surface?: Surface;
  launcher?: Launcher;
  /** Extra headers, e.g. auth for the app surface. */
  headers?: Record<string, string>;
}

interface Crew { key: string; name: string; image: string }
interface OpenOptions { message?: string }
interface Hours { timezone: string; opens_at: string; closes_at: string; days: number[]; reply_time_copy: string; is_open: boolean }
interface Attachment { uuid: string; kind: string; url: string; mime_type: string }
interface Message {
  id: number;
  author: 'visitor' | 'agent' | 'system';
  body: string;
  agent: { name: string; face: string; image: string | null } | null;
  attachments: Attachment[];
  created_at: string | null;
  /** client-only */
  pending?: boolean;
  failed?: boolean;
  localId?: string;
}
interface Conversation {
  uuid: string;
  status: string;
  email: string | null;
  ask_email: boolean;
  last_visitor_seen_at: string | null;
  unread_count: number;
  messages: Message[];
}
interface Bootstrap { enabled: boolean; hours: Hours; crew: Crew[]; surface: Surface | null; conversation: Conversation | null }

interface PendingUpload { localId: string; uuid: string | null; previewUrl: string; uploading: boolean; failed: boolean }

const ANON_KEY = 'plandalf_anon';
const FRAGMENT_KEY = 'plandalf-support';
const POLL_OPEN_MS = 5000;
const POLL_CLOSED_MS = 30000;

const ICONS = {
  image: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"></rect><circle cx="9" cy="10" r="1.8"></circle><path d="M21 16l-5-5-9 9"></path></svg>',
  arrow: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="M13 6l6 6-6 6"></path></svg>',
  close: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"></path><path d="M6 6l12 12"></path></svg>',
  mail: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"></rect><path d="M3 7l9 6 9-6"></path></svg>',
  check: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#21a179" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"></path></svg>',
  chat: '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1a1408" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8 8 0 0 1-8 8H8l-5 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z"></path></svg>',
};

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function storage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** Escape, then turn bare URLs into links. */
function richText(text: string): string {
  return escapeHtml(text).replace(/(https?:\/\/[^\s<]+)/g, (url) => {
    const trimmed = url.replace(/[.,)]+$/, '');
    const tail = url.slice(trimmed.length);
    return `<a href="${trimmed}" target="_blank" rel="noopener">${trimmed}</a>${tail}`;
  });
}

function timeLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '');
  if (sameDay) return time;
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `yesterday, ${time}`;
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${time}`;
}

/** Hours helpers: everything computed from the visitor's clock in our timezone. */
function localParts(hours: Hours, at: Date): { weekday: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: hours.timezone, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday')) + 1;
  const hour = parseInt(get('hour'), 10) % 24;
  const minute = parseInt(get('minute'), 10);
  return { weekday, minutes: hour * 60 + minute };
}
function toMinutes(hhmm: string): number { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; }
function isOpenNow(hours: Hours): boolean {
  const { weekday, minutes } = localParts(hours, new Date());
  return hours.days.includes(weekday) && minutes >= toMinutes(hours.opens_at) && minutes < toMinutes(hours.closes_at);
}
function fmtClock(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = ((h + 11) % 12) + 1;
  return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
}
function tzAbbr(hours: Hours): string {
  const parts = new Intl.DateTimeFormat('en-AU', { timeZone: hours.timezone, timeZoneName: 'short' }).formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? hours.timezone;
}
/** "in 5h 46m" until the next opening, or "" when we cannot tell. */
function untilOpen(hours: Hours): string {
  const now = new Date();
  const { weekday, minutes } = localParts(hours, now);
  const opens = toMinutes(hours.opens_at);
  for (let d = 0; d < 8; d++) {
    const day = ((weekday - 1 + d) % 7) + 1;
    if (!hours.days.includes(day)) continue;
    const delta = d * 1440 + opens - minutes;
    if (delta <= 0) continue;
    const h = Math.floor(delta / 60); const m = delta % 60;
    return h >= 24 ? `in ${Math.round(h / 24)}d` : h ? `in ${h}h ${m}m` : `in ${m}m`;
  }
  return '';
}

class SupportWidget {
  private opts: Required<Pick<InitOptions, 'apiBase' | 'apiPath' | 'theme' | 'surface' | 'launcher'>> & { headers: Record<string, string> };
  private root!: ShadowRoot;
  private host!: HTMLElement;
  private launcherEl!: HTMLButtonElement;
  private panelEl!: HTMLDivElement;
  private threadEl!: HTMLDivElement;
  private headEl!: HTMLDivElement;
  private textarea!: HTMLTextAreaElement;
  /** The last message open() typed in; replaced by the next one until the visitor edits it. */
  private lastPrefill: string | null = null;
  private sendBtn!: HTMLButtonElement;
  private chipsEl!: HTMLDivElement;
  private fileInput!: HTMLInputElement;

  private anonId: string;
  private boot: Bootstrap | null = null;
  private conversation: Conversation | null = null;
  private messages: Message[] = [];
  private uploads: PendingUpload[] = [];
  private isOpen = false;
  private unread = 0;
  private emailSaved = false;
  private emailError = '';
  private unreadDividerBefore: number | null = null;
  private pollTimer: number | null = null;
  private mounted = false;
  private fragmentOpen = false;

  constructor(options: InitOptions) {
    const script = document.currentScript as HTMLScriptElement | null;
    const scriptOrigin = script?.src ? new URL(script.src, location.href).origin : '';
    this.opts = {
      apiBase: (options.apiBase ?? scriptOrigin ?? '').replace(/\/$/, ''),
      apiPath: options.apiPath ?? (options.surface === 'app' ? '/app/support' : '/api/support'),
      theme: options.theme ?? 'dark',
      surface: options.surface ?? 'site',
      launcher: options.launcher ?? (options.surface === 'app' ? 'compact' : 'pill'),
      headers: options.headers ?? {},
    };
    this.anonId = this.resolveAnonId();
  }

  // ---------------------------------------------------------------------
  // Identity
  // ---------------------------------------------------------------------

  private resolveAnonId(): string {
    const store = storage();
    const fromFragment = this.readFragment();
    if (fromFragment?.anon) {
      store?.setItem(ANON_KEY, fromFragment.anon);
      this.fragmentOpen = true;
      return fromFragment.anon;
    }
    let id = store?.getItem(ANON_KEY) ?? '';
    if (!/^[0-9a-f-]{36}$/i.test(id)) {
      id = uuid();
      store?.setItem(ANON_KEY, id);
    }
    return id.toLowerCase();
  }

  private readFragment(): { conversation: string; anon: string } | null {
    const match = location.hash.match(new RegExp(`${FRAGMENT_KEY}=([0-9a-f-]{36}):([0-9a-f-]{36})?`, 'i'));
    if (!match) return null;
    history.replaceState(null, '', location.pathname + location.search);
    return { conversation: match[1], anon: (match[2] ?? '').toLowerCase() };
  }

  // ---------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------

  private async api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Plandalf-Anon': this.anonId,
      ...this.opts.headers,
      ...(init.headers as Record<string, string> | undefined),
    };
    if (init.body && !(init.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    if (this.opts.surface === 'app') {
      const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
      if (xsrf) headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrf[1]);
    }
    const res = await fetch(`${this.opts.apiBase}${this.opts.apiPath}${path}`, { ...init, headers, credentials: this.opts.surface === 'app' ? 'include' : 'omit' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data?.message || (data?.errors ? Object.values(data.errors).flat().join(' ') : `Request failed (${res.status})`);
      throw new Error(message);
    }
    return data as T;
  }

  // ---------------------------------------------------------------------
  // Mount
  // ---------------------------------------------------------------------

  async mount(): Promise<void> {
    if (this.mounted) return;
    this.mounted = true;
    this.ensureFonts();

    this.host = document.createElement('div');
    this.host.id = 'plandalf-support';
    this.root = this.host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = styles;
    this.root.appendChild(style);

    const wrap = document.createElement('div');
    wrap.className = 'ps';
    wrap.dataset.theme = this.opts.theme;
    this.root.appendChild(wrap);

    this.launcherEl = document.createElement('button');
    this.launcherEl.type = 'button';
    this.launcherEl.className = `ps-launcher${this.opts.launcher === 'compact' ? ' ps-compact' : ''}`;
    this.launcherEl.setAttribute('aria-label', 'Chat with the Plandalf team');
    this.launcherEl.addEventListener('click', () => this.toggle());
    wrap.appendChild(this.launcherEl);

    this.panelEl = document.createElement('div');
    this.panelEl.className = 'ps-panel';
    this.panelEl.setAttribute('role', 'dialog');
    this.panelEl.setAttribute('aria-label', 'Chat with the Plandalf team');
    this.panelEl.hidden = true;
    wrap.appendChild(this.panelEl);
    this.buildPanel();

    document.body.appendChild(this.host);
    this.renderLauncher();

    document.addEventListener('visibilitychange', () => this.schedulePoll());
    // Astro view transitions swap <body> on navigation; put the widget back.
    document.addEventListener('astro:after-swap', () => {
      if (!document.body.contains(this.host)) document.body.appendChild(this.host);
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    await this.loadBootstrap();
  }

  private ensureFonts(): void {
    const id = 'plandalf-support-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@1,9..144,400;1,9..144,500&family=Geist:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }

  private async loadBootstrap(): Promise<void> {
    try {
      this.boot = await this.api<Bootstrap>('/bootstrap');
    } catch {
      this.boot = null;
      return;
    }
    if (!this.boot.enabled) {
      this.launcherEl.hidden = true;
      return;
    }
    if (this.boot.conversation) {
      this.adoptConversation(this.boot.conversation);
      this.unread = this.boot.conversation.unread_count;
      if (this.unread > 0) {
        const firstUnread = this.messages.filter((m) => m.author === 'agent').slice(-this.unread)[0];
        this.unreadDividerBefore = firstUnread?.id ?? null;
      }
    }
    this.renderLauncher();
    this.renderHead();
    this.renderThread();
    if (this.unread > 0 || this.fragmentOpen) this.open();
    this.schedulePoll();
  }

  private adoptConversation(conversation: Conversation): void {
    this.conversation = conversation;
    this.messages = conversation.messages.slice();
    this.emailSaved = conversation.email !== null;
  }

  // ---------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------

  /** `message` pre-fills the composer when it is empty or still holds the previous pre-fill; the visitor still presses send. */
  open(options: OpenOptions = {}): void {
    if (!this.mounted) return;
    this.isOpen = true;
    this.panelEl.hidden = false;
    this.launcherEl.hidden = true;
    this.renderHead();
    this.renderThread();
    const current = this.textarea.value.trim();
    if (options.message && (current === '' || current === this.lastPrefill?.trim())) {
      this.textarea.value = options.message;
      this.lastPrefill = options.message;
      this.syncComposer();
    }
    this.scrollToEnd();
    this.textarea.focus();
    this.markSeen();
    this.schedulePoll();
    this.emit('opened');
  }

  close(): void {
    this.isOpen = false;
    this.panelEl.hidden = true;
    this.launcherEl.hidden = false;
    this.unreadDividerBefore = null;
    this.renderLauncher();
    this.schedulePoll();
    this.emit('closed');
  }

  toggle(): void { this.isOpen ? this.close() : this.open(); }

  /** Retract the whole widget (launcher and panel), e.g. on a page that should not show it. */
  hide(): void {
    if (!this.mounted) return;
    if (this.isOpen) this.close();
    this.host.hidden = true;
  }

  show(): void {
    if (!this.mounted) return;
    this.host.hidden = false;
  }

  private emit(type: string, detail: Record<string, unknown> = {}): void {
    document.dispatchEvent(new CustomEvent('plandalf:support', { detail: { type, ...detail } }));
  }

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  private crewFaces(count: number): string {
    const crew = this.boot?.crew ?? [];
    return crew.slice(0, count).map((c) => `<img src="${c.image}" alt="${escapeHtml(c.name)}" loading="lazy">`).join('');
  }

  private renderLauncher(): void {
    const label = this.unread > 0 ? `${this.unread === 1 ? 'A human replied' : 'Humans replied'}` : 'Summon a human';
    const badge = this.unread > 0 ? `<span class="ps-badge">${this.unread}</span>` : '';
    if (this.opts.launcher === 'compact') {
      const face = this.boot?.crew[0];
      this.launcherEl.innerHTML = `<span class="ps-faces">${face ? `<img src="${face.image}" alt="">` : ''}</span><span class="ps-chat">${ICONS.chat}</span>${badge}`;
      return;
    }
    this.launcherEl.innerHTML = `<span class="ps-faces">${this.crewFaces(3)}</span><span class="ps-label">${label}</span>${badge}`;
  }

  private hoursOpen(): boolean {
    return this.boot ? isOpenNow(this.boot.hours) : true;
  }

  private renderHead(): void {
    const hours = this.boot?.hours;
    const open = this.hoursOpen();
    this.headEl.classList.toggle('ps-away', !open);
    let title = 'Speak, friend, and enter.';
    let sub = 'One does not simply talk to a bot. Every reply is typed by one of us.';
    if (hours) {
      const tz = tzAbbr(hours);
      if (open) {
        sub = `<span class="ps-dot"></span><b>Crew online</b> &middot; ${fmtClock(hours.opens_at)} to ${fmtClock(hours.closes_at)} ${tz} &middot; ${escapeHtml(hours.reply_time_copy)}`;
      } else {
        title = 'The Fellowship rests.';
        const until = untilOpen(hours);
        sub = `<b>Crew asleep</b> &middot; back at ${fmtClock(hours.opens_at)} ${tz}${until ? `, ${until}` : ''} &middot; leave a message`;
      }
    }
    this.headEl.innerHTML = `
      <div class="ps-faces">${this.crewFaces(5)}</div>
      <h2 class="ps-title">${title}</h2>
      <p class="ps-sub">${sub}</p>`;
  }

  private renderMessage(m: Message): string {
    const images = m.attachments.filter((a) => a.kind === 'image');
    const imagesHtml = images.length
      ? `<div class="ps-images">${images.map((a) => `<a href="${a.url}" target="_blank" rel="noopener"><img src="${a.url}" alt=""></a>`).join('')}</div>`
      : '';
    const body = m.body ? `<div class="ps-bubble">${richText(m.body)}</div>` : '';
    const cls = `ps-row ${m.author === 'visitor' ? 'ps-visitor' : ''} ${m.pending ? 'ps-sending' : ''} ${m.failed ? 'ps-failed' : ''}`;
    if (m.author === 'agent') {
      const image = m.agent?.image ?? this.boot?.crew[0]?.image ?? '';
      return `<div class="${cls}">
        <img class="ps-avatar" src="${image}" alt="">
        <div class="ps-col">${body}${imagesHtml}<div class="ps-meta"><i>${escapeHtml(m.agent?.name ?? 'The crew')}, human</i><span>&middot; ${timeLabel(m.created_at)}</span></div></div>
      </div>`;
    }
    const meta = m.failed ? 'Did not send. Tap to retry.' : m.pending ? 'Sending' : timeLabel(m.created_at);
    return `<div class="${cls}" ${m.failed ? `data-retry="${m.localId}"` : ''}>
      <div class="ps-col">${body}${imagesHtml}<div class="ps-meta">${meta}</div></div>
    </div>`;
  }

  private renderEmailCard(): string {
    if (!this.conversation) return '';
    if (this.opts.surface === 'app') {
      return `<div class="ps-card">
      <div class="ps-faces">${this.crewFaces(2)}</div>
      <div class="ps-card-body"><p>Got it, one of us will pick this up. Replies land here and in your inbox.</p></div>
    </div>`;
    }
    if (this.emailSaved) {
      return `<div class="ps-line"><span>${ICONS.check} We will email ${escapeHtml(this.conversation.email ?? 'you')} if you are not here</span></div>`;
    }
    if (!this.conversation.ask_email) return '';
    const open = this.hoursOpen();
    const hours = this.boot?.hours;
    const copy = open
      ? 'Got it, one of us will pick this up. Where should we reply if you step away?'
      : `Saved. We are away until ${hours ? fmtClock(hours.opens_at) + ' ' + tzAbbr(hours) : 'the morning'}, so we will answer by email. Where should we reply?`;
    return `<div class="ps-card">
      <div class="ps-faces">${this.crewFaces(2)}</div>
      <div class="ps-card-body">
        <p>${copy}</p>
        <form class="ps-email" data-email-form>
          <div class="ps-email-row">
            <input type="email" name="email" placeholder="you@company.com" autocomplete="email" required>
            <button type="submit">Notify me</button>
          </div>
          ${this.emailError ? `<div class="ps-error">${escapeHtml(this.emailError)}</div>` : !open ? '<div class="ps-hint">Needed while we are away</div>' : ''}
        </form>
      </div>
    </div>`;
  }

  private renderThread(): void {
    if (this.messages.length === 0) {
      this.threadEl.innerHTML = `<div class="ps-empty">Tell us what you are stuck on. Screenshots welcome.</div>`;
      return;
    }
    const parts: string[] = [];
    const firstVisitorId = this.messages.find((m) => m.author === 'visitor')?.id;
    let cardPlaced = false;
    for (const m of this.messages) {
      if (this.unreadDividerBefore !== null && m.id === this.unreadDividerBefore) {
        parts.push(`<div class="ps-divider"><span>${escapeHtml(m.agent?.name ?? 'Someone')} replied while you were away</span></div>`);
      }
      parts.push(this.renderMessage(m));
      if (!cardPlaced && m.id === firstVisitorId) {
        parts.push(this.renderEmailCard());
        cardPlaced = true;
      }
    }
    this.threadEl.innerHTML = parts.join('');
    const form = this.threadEl.querySelector<HTMLFormElement>('[data-email-form]');
    form?.addEventListener('submit', (e) => { e.preventDefault(); void this.submitEmail(form); });
    this.threadEl.querySelectorAll<HTMLElement>('[data-retry]').forEach((el) => {
      el.addEventListener('click', () => void this.retry(el.dataset.retry!));
    });
  }

  private scrollToEnd(): void {
    requestAnimationFrame(() => { this.threadEl.scrollTop = this.threadEl.scrollHeight; });
  }

  private buildPanel(): void {
    this.panelEl.innerHTML = `
      <button type="button" class="ps-close" aria-label="Close">${ICONS.close}</button>
      <div class="ps-head"></div>
      <div class="ps-thread" aria-live="polite"></div>
      <div class="ps-composer">
        <div class="ps-box">
          <textarea rows="1" placeholder="Write a message" aria-label="Your message"></textarea>
          <div class="ps-chips" hidden></div>
          <div class="ps-tools">
            <button type="button" class="ps-attach" aria-label="Attach an image">${ICONS.image}</button>
            <button type="button" class="ps-send" disabled>Send ${ICONS.arrow}</button>
          </div>
        </div>
        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple class="ps-sr" tabindex="-1">
        <div class="ps-foot">Replies are typed by a person, not generated.</div>
      </div>`;
    this.headEl = this.panelEl.querySelector('.ps-head')!;
    this.threadEl = this.panelEl.querySelector('.ps-thread')!;
    this.textarea = this.panelEl.querySelector('textarea')!;
    this.sendBtn = this.panelEl.querySelector('.ps-send')!;
    this.chipsEl = this.panelEl.querySelector('.ps-chips')!;
    this.fileInput = this.panelEl.querySelector('input[type=file]')!;

    this.panelEl.querySelector('.ps-close')!.addEventListener('click', () => this.close());
    this.panelEl.querySelector('.ps-attach')!.addEventListener('click', () => this.fileInput.click());
    this.fileInput.addEventListener('change', () => { void this.addFiles(Array.from(this.fileInput.files ?? [])); this.fileInput.value = ''; });
    this.textarea.addEventListener('input', () => this.syncComposer());
    this.textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void this.send(); }
    });
    this.textarea.addEventListener('paste', (e) => {
      const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (files.length) { e.preventDefault(); void this.addFiles(files); }
    });
    this.sendBtn.addEventListener('click', () => void this.send());
  }

  private syncComposer(): void {
    this.textarea.style.height = 'auto';
    this.textarea.style.height = `${Math.min(this.textarea.scrollHeight, 120)}px`;
    const hasText = this.textarea.value.trim().length > 0;
    const uploading = this.uploads.some((u) => u.uploading);
    const hasUploads = this.uploads.some((u) => u.uuid);
    this.sendBtn.disabled = uploading || (!hasText && !hasUploads);
  }

  private renderChips(): void {
    this.chipsEl.hidden = this.uploads.length === 0;
    this.chipsEl.innerHTML = this.uploads.map((u) => `
      <div class="ps-chip ${u.uploading ? 'ps-uploading' : ''}" title="${u.failed ? 'Upload failed' : ''}">
        <img src="${u.previewUrl}" alt="">
        <button type="button" data-remove="${u.localId}" aria-label="Remove">&times;</button>
      </div>`).join('');
    this.chipsEl.querySelectorAll<HTMLElement>('[data-remove]').forEach((el) => {
      el.addEventListener('click', () => {
        this.uploads = this.uploads.filter((u) => u.localId !== el.dataset.remove);
        this.renderChips();
        this.syncComposer();
      });
    });
    this.syncComposer();
  }

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------

  private async addFiles(files: File[]): Promise<void> {
    for (const file of files.slice(0, 5 - this.uploads.length)) {
      const upload: PendingUpload = { localId: uuid(), uuid: null, previewUrl: URL.createObjectURL(file), uploading: true, failed: false };
      this.uploads.push(upload);
      this.renderChips();
      const form = new FormData();
      form.append('file', file);
      if (this.conversation) form.append('conversation', this.conversation.uuid);
      try {
        const res = await this.api<{ uuid: string }>('/attachments', { method: 'POST', body: form });
        upload.uuid = res.uuid;
      } catch {
        upload.failed = true;
      } finally {
        upload.uploading = false;
        this.renderChips();
      }
    }
  }

  private async send(): Promise<void> {
    const body = this.textarea.value.trim();
    const attachments = this.uploads.filter((u) => u.uuid).map((u) => u.uuid!);
    if ((!body && attachments.length === 0) || this.sendBtn.disabled) return;

    const local: Message = {
      id: Number.MAX_SAFE_INTEGER,
      localId: uuid(),
      author: 'visitor',
      body,
      agent: null,
      attachments: this.uploads.filter((u) => u.uuid).map((u) => ({ uuid: u.uuid!, kind: 'image', url: u.previewUrl, mime_type: 'image/*' })),
      created_at: new Date().toISOString(),
      pending: true,
    };
    this.messages.push(local);
    this.textarea.value = '';
    this.uploads = [];
    this.renderChips();
    this.renderThread();
    this.scrollToEnd();
    await this.deliver(local, attachments);
  }

  private async deliver(local: Message, attachments: string[]): Promise<void> {
    try {
      let saved: Message;
      if (!this.conversation) {
        const res = await this.api<{ conversation: Conversation }>('/conversations', {
          method: 'POST',
          body: JSON.stringify({ body: local.body, attachments, source_url: location.href }),
        });
        this.adoptConversation(res.conversation);
        saved = this.messages[this.messages.length - 1];
        this.emit('started', { conversation: res.conversation.uuid });
      } else {
        const res = await this.api<{ message: Message }>(`/conversations/${this.conversation.uuid}/messages`, {
          method: 'POST',
          body: JSON.stringify({ body: local.body, attachments }),
        });
        saved = res.message;
        this.messages = this.messages.map((m) => (m.localId === local.localId ? saved : m));
      }
      this.emit('sent', { message: saved.id });
      this.renderThread();
      this.scrollToEnd();
      this.schedulePoll();
    } catch (err) {
      local.failed = true;
      local.pending = false;
      (local as Message & { retryAttachments?: string[] }).retryAttachments = attachments;
      this.renderThread();
    }
  }

  private async retry(localId: string): Promise<void> {
    const m = this.messages.find((x) => x.localId === localId) as (Message & { retryAttachments?: string[] }) | undefined;
    if (!m) return;
    m.failed = false;
    m.pending = true;
    this.renderThread();
    await this.deliver(m, m.retryAttachments ?? []);
  }

  private async submitEmail(form: HTMLFormElement): Promise<void> {
    if (!this.conversation) return;
    const input = form.querySelector<HTMLInputElement>('input[name=email]')!;
    const button = form.querySelector<HTMLButtonElement>('button')!;
    button.disabled = true;
    try {
      const res = await this.api<{ email: string }>(`/conversations/${this.conversation.uuid}/email`, {
        method: 'POST',
        body: JSON.stringify({ email: input.value.trim() }),
      });
      this.conversation.email = res.email;
      this.emailSaved = true;
      this.emailError = '';
      this.emit('email', { email: res.email });
    } catch (err) {
      this.emailError = err instanceof Error ? err.message : 'That did not work, try again.';
      button.disabled = false;
    }
    this.renderThread();
  }

  private async markSeen(): Promise<void> {
    if (!this.conversation) return;
    this.unread = 0;
    this.renderLauncher();
    try { await this.api(`/conversations/${this.conversation.uuid}/seen`, { method: 'POST' }); } catch { /* best effort */ }
  }

  // ---------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------

  private schedulePoll(): void {
    if (this.pollTimer) { window.clearTimeout(this.pollTimer); this.pollTimer = null; }
    if (!this.conversation || document.visibilityState === 'hidden') return;
    this.pollTimer = window.setTimeout(() => void this.poll(), this.isOpen ? POLL_OPEN_MS : POLL_CLOSED_MS);
  }

  private async poll(): Promise<void> {
    if (!this.conversation) return;
    const lastId = this.messages.filter((m) => !m.pending && !m.failed && m.id !== Number.MAX_SAFE_INTEGER).reduce((max, m) => Math.max(max, m.id), 0);
    try {
      const res = await this.api<{ messages: Message[]; status: string }>(`/conversations/${this.conversation.uuid}/messages?after=${lastId}`);
      const fresh = res.messages.filter((m) => !this.messages.some((x) => x.id === m.id));
      if (fresh.length) {
        const agentReplies = fresh.filter((m) => m.author === 'agent');
        if (!this.isOpen && agentReplies.length) {
          if (this.unread === 0) this.unreadDividerBefore = agentReplies[0].id;
          this.unread += agentReplies.length;
          this.renderLauncher();
          this.emit('reply', { count: agentReplies.length });
        }
        this.messages.push(...fresh);
        this.renderThread();
        if (this.isOpen) {
          this.scrollToEnd();
          if (agentReplies.length) void this.markSeen();
        }
      }
    } catch { /* try again next tick */ }
    this.schedulePoll();
  }
}

// -----------------------------------------------------------------------
// Public surface
// -----------------------------------------------------------------------

let instance: SupportWidget | null = null;

function init(options: InitOptions = {}): SupportWidget {
  if (instance) {
    instance.show();
    return instance;
  }
  instance = new SupportWidget(options);
  const start = () => void instance!.mount();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  return instance;
}

const PlandalfSupport = {
  init,
  open: (options?: OpenOptions) => instance?.open(options),
  close: () => instance?.close(),
  toggle: () => instance?.toggle(),
  show: () => instance?.show(),
  hide: () => instance?.hide(),
};

declare global {
  interface Window { PlandalfSupport: typeof PlandalfSupport }
}
window.PlandalfSupport = PlandalfSupport;

// Auto-init from the script tag's data attributes unless told not to.
const currentScript = document.currentScript as HTMLScriptElement | null;
if (currentScript && currentScript.dataset.manual === undefined) {
  init({
    apiBase: currentScript.dataset.api,
    theme: (currentScript.dataset.theme as Theme | undefined) ?? 'dark',
    surface: (currentScript.dataset.surface as Surface | undefined) ?? 'site',
    launcher: currentScript.dataset.launcher as Launcher | undefined,
    apiPath: currentScript.dataset.apiPath,
  });
}

export { PlandalfSupport };
